import { isInputJobUnblocked, InputJob } from "app/components/build/Show/lib/types";
import { useMostRecentJob } from "app/components/build/Show/lib/BuildContext";
import {
  UnblockButton,
  isInputNodeDisabled,
  InputStepHoverCard,
} from "app/components/Playground/Node/InputStepNode";
import { BlockStep, InputStep, Outcome, State } from "app/lib/pipeline";
import { useCallback } from "react";
import { StateIcon } from "app/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "app/components/Playground/Node/colors";
import { twMerge } from "tailwind-merge";
import Emojify from "app/components/shared/Emojify";
import classNames from "classnames";
import { useTrack } from "app/components/build/Show/lib/useTrack";
import { resolveStepUrl } from "app/components/build/Show/lib/urlForView";
import useCurrentJobOrStep from "app/components/build/Show/lib/useCurrentJobOrStep";
import { StepLink } from "./StepLink";
import UserAvatar from "app/components/shared/UserAvatar";
import * as HoverCard from "app/components/Playground/Node/StepHoverCard";

interface InputStepListItemProps {
  step: InputStep | BlockStep;
  depth?: number;
}

export function InputStepListItem({ step, depth = 0 }: InputStepListItemProps) {
  const job = useMostRecentJob<InputJob>(step.uuid);
  const { currentStep } = useCurrentJobOrStep();
  const track = useTrack();

  const url = resolveStepUrl(step.uuid);
  const label = step.label || "Continue";

  const onClick = useCallback(() => {
    track("Build Sidebar Step Clicked", { type: step.type });
  }, [url]);

  if (!job) {
    return null;
  }

  if (isInputJobUnblocked(job)) {
    return (
      <HoverCard.Root>
        <HoverCard.Portal>
          <InputStepHoverCard
            step={step}
            job={job}
            className="w-fit"
            collisionPadding={5}
            side="right"
          />
        </HoverCard.Portal>

        <HoverCard.Trigger asChild={true}>
          <div>
            <StepLink
              to={url}
              onClick={onClick}
              active={step.uuid === currentStep?.uuid}
              depth={depth}
            >
              <div className="flex min-w-0 flex-auto py-1">
                <div className="w-full max-w-full truncate leading-normal">
                  <Emojify text={label} />
                </div>
              </div>

              <span className="flex w-5 shrink-0 items-center justify-center self-stretch">
                <UserAvatar
                  user={{
                    name: job.unblockedBy.name,
                    avatar: {
                      url: job.unblockedBy.avatarUrl,
                    },
                  }}
                />
              </span>

              <div className="flex shrink-0 items-center justify-center self-stretch">
                <StateIcon
                  state={State.Finished}
                  outcome={Outcome.Passed}
                  className={twMerge(
                    "-mr-0.5",
                    textColorForStep({
                      state: State.Finished,
                      outcome: Outcome.Passed,
                    }),
                  )}
                />
              </div>
            </StepLink>
          </div>
        </HoverCard.Trigger>
      </HoverCard.Root>
    );
  }

  if (isInputNodeDisabled(job)) {
    return (
      <StepLink
        to={url}
        onClick={onClick}
        active={step.uuid === currentStep?.uuid}
        depth={depth}
        className="cursor-not-allowed"
        disabled={true}
      >
        <div className="relative flex min-w-0 flex-auto items-baseline justify-start text-left font-medium opacity-50">
          <div className="flex min-w-0 flex-auto py-1">
            <div className="w-full max-w-full truncate leading-normal">
              <Emojify text={label} />
            </div>
          </div>

          <span className="flex shrink-0 items-center justify-center self-stretch">
            <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs font-medium">
              Input required
            </span>
          </span>
        </div>
      </StepLink>
    );
  }

  return (
    <div onClick={onClick} className="rounded-md hover:bg-purple-100/50-white">
      <UnblockButton
        job={job}
        className={classNames("unstyled-button w-full", {
          "pl-2": depth === 0,
          "pl-7": depth === 1,
        })}
      >
        <div className="relative flex min-w-0 flex-auto items-baseline justify-start text-left font-medium">
          <div className="flex min-w-0 flex-auto py-1">
            <div className="w-full max-w-full truncate leading-normal">
              <Emojify text={label} />
            </div>
          </div>

          <span className="flex shrink-0 items-center justify-center self-stretch">
            <span className="rounded-sm bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-600">
              Input required
            </span>
          </span>
        </div>
      </UnblockButton>
    </div>
  );
}
